import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { fetchOrgNodeTypes, fetchOrgTree, type OrgNode, type OrgNodeType } from '@/features/org/orgApi'
import { employeeNodes } from './hrApi'

export interface EmployeeNodesApi {
  /**
   * BUTUN ko'rinadigan daraxt — ota-bobolar ham.
   *
   * ⚠️ Faqat tanlanadiganlar EMAS: tanlagich tuzilmani ko'rsatishi
   * kerak ("Texnik bo'lim" qaysi filial ostida), aks holda bo'limlar
   * ildizga ko'chib, daraxt YOLG'ON ko'rinardi.
   */
  nodes: readonly OrgNode[]
  types: readonly OrgNodeType[]
  /** Xodim biriktirilishi mumkin bo'lganlar — `hasEmployees` bo'yicha */
  selectable: readonly OrgNode[]
  loading: boolean
  /**
   * Ro'yxat ISHONCHLImi.
   *
   * ⚠️ `false` bo'lganda BO'SH ro'yxat "tugun yo'q" DEGANI EMAS —
   * "javob yo'q" degani. Ikkalasini bir xil ko'rsatish `useSiteIndex`
   * da hujjatlashtirilgan xatoni takrorlardi: operator bo'sh tanlovni
   * ko'rib "bizda bo'lim ochilmagan ekan" degan noto'g'ri xulosa
   * chiqarardi.
   */
  reliable: boolean
  /** Ro'yxat yo'qligining SABABI — ekranda aynan shu ko'rsatiladi */
  reason: 'ok' | 'no-permission' | 'failed'
}

/**
 * Xodim biriktirish uchun tashkiliy tugunlar.
 *
 * <h2>⚠️ MA'LUMOT `platform` DAN KELADI, `xsib-hr` DAN EMAS</h2>
 * `QARORLAR.md` §20.4: "daraxt bitta egada". `xsib-hr` da tugun
 * endpointi ataylab yo'q — HR `parent_id` bo'ylab O'ZI yurmaydi.
 *
 * Kesh kaliti ham `platform` niki (`queryKeys.org.*`), ya'ni Tashkiliy
 * tuzilma sahifasi bilan BITTA kesh yozuvi bo'lishiladi va ikkinchi
 * so'rov ketmaydi.
 *
 * <h2>⚠️ HUQUQ CHEGARASI — VA U OCHIQ AYTILADI</h2>
 * `GET /api/org-nodes` `org.node.read` talab qiladi. Faqat
 * `hr.person.read` bor operator:
 *   · xodimlar RO'YXATINI to'liq ko'radi (tugun nomi javobning
 *     O'ZIDA keladi — `PersonView.orgNodeName`);
 *   · tugun bo'yicha FILTRLAY olmaydi va yangi tayinlash BERA olmaydi.
 *
 * So'rov shunday holatda UMUMAN yuborilmaydi (`enabled`) — 403 ni
 * kutib, keyin uni yashirish keraksiz shovqin bo'lardi.
 */
export function useEmployeeNodes(enabled = true): EmployeeNodesApi {
  const { can } = usePermissions()
  const allowed = can('org.node.read')
  const active = enabled && allowed

  const tree = useQuery({
    queryKey: queryKeys.org.tree,
    queryFn: ({ signal }) => fetchOrgTree(signal),
    enabled: active,
  })

  const types = useQuery({
    queryKey: queryKeys.org.types,
    queryFn: fetchOrgNodeTypes,
    enabled: active,
    // Tur ta'rifi kamdan-kam o'zgaradi; o'zgarganda `org.all` prefiksi
    // uni baribir bekor qiladi.
    staleTime: 5 * 60_000,
  })

  const nodes = useMemo(() => tree.data?.nodes ?? [], [tree.data])
  const typeList = useMemo(() => types.data ?? [], [types.data])
  const selectable = useMemo(() => employeeNodes(nodes, typeList), [nodes, typeList])

  const failed = tree.isError || types.isError
  const loading = active && (tree.isPending || types.isPending)

  return {
    nodes,
    types: typeList,
    selectable,
    loading,
    reliable: active && !failed && !loading,
    reason: !allowed ? 'no-permission' : failed ? 'failed' : 'ok',
  }
}
